import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Column, Row, Typography } from '@tempmee/react';

import { fetchUsersForMap } from '../../actions/mapActions';
import { useGetInternalUserMe } from '../../domain/Users';
import hygienistIcon from '../../images/hygienist.png';
import dntIcon from '../../images/tooth.png';
import greenCheckIcon from '../../images/green-check.png';
import welcomeImage from '../../images/welcomeImage.png';

export default () => {
  const dispatch = useDispatch();
  const {
    dentistCount,
    hygienistCount,
    completedJobsCount,
    associateDentistCount,
    dentalAssistantCount,
  } = useSelector((state) => state.userMap);
  const adminUser = useSelector((state) => state.users.adminUser);
  // Role is not on the auth/me payload and deliberately not in the JWT either
  // (GOTU-6359) — auth-service puts the derived claims in the token and serves
  // the slug from its own route, so this is a second request by design.
  const { roleLabel } = useGetInternalUserMe();

  // These counts used to be fetched by Dashboard on every route. This is the
  // only page that reads them, so it fetches them itself.
  useEffect(() => {
    dispatch(fetchUsersForMap());
  }, [dispatch]);

  // `mt-3 mr-3` became this Row's padding: the design system ships no margin
  // utilities (`mr-*` is not in the compiled stylesheet at all), so the card's
  // offset from the top-right corner has to come from a parent's padding rather
  // than from the card's own margin.
  //
  // `alignSelf="stretch"` is load-bearing, not decoration. The page's outer
  // container is a column flex parent with `align-items: center`; without the
  // override this Row is centred by it, and — being shrink-to-fit in a column —
  // has no free space for `justify="end"` to distribute, so the card lands in
  // the middle of the page. Stretching it across the cross axis is what
  // reproduces the `alignSelf: 'flex-end'` this replaced.
  //
  // `bg-primary-50` replaces #D8F8FF. It is the nearest token — very slightly
  // paler — and the DS rule is to match the intent through a token rather than
  // reproduce a hex. `rounded-pill` is exactly the 25px radius this had.
  const adminInfo = () => (
    <Row alignSelf="stretch" justify="end" pt={4} pr={4}>
      {/* `p={5}` is the 1.25rem `.card-body` supplied, and `gap={1}` the
          0.25rem the email's `mt-1` did.

          Both lines are 1rem, which is what this card always rendered at: the
          design system's stylesheet reverts `h1`-`h6` to the user-agent sizes,
          so the `<h4>`s here were never heading-sized on screen. `subtitle1`
          (1rem bold) and `body1` (1rem regular) reproduce that. A heading
          variant does not — `h5` is 1.5rem, and its responsive `md:text-[2rem]`
          made the label twice the size of the original on any desktop
          viewport. */}
      <Box className="bg-primary-50 rounded-pill" p={5}>
        <Column gap={1}>
          <Typography variant="subtitle1">Current Admin Account:</Typography>
          <Typography variant="body1">{adminUser?.email_id}</Typography>
          {/* Left out entirely until the query settles, rather than shown as a
              placeholder that changes a beat later. `roleLabel` is null only
              while pending — on failure the hook resolves it to "Unknown". */}
          {roleLabel ? <Typography variant="body1">Role: {roleLabel}</Typography> : null}
        </Column>
      </Box>
    </Row>
  );
  return (
    <div
      className="card border-0"
      style={{
        display: 'flex',
        alighItems: 'center',
        width: '100%',
        justifyContent: 'center',
        flexDirection: 'column',
        alignItems: 'center',
        overflowY: 'scroll',
      }}
    >
      {adminInfo()}

      <div className="card-body">
        <div className="card" style={{ marginTop: 10, maxWidth: 750, borderRadius: 25 }}>
          <div
            className="card-body"
            style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column' }}
          >
            <h3 style={{ marginTop: 20, textAlign: 'center' }}>General Information:</h3>
            <div style={styles.infoLine}>
              <div style={styles.infoLinePic}>
                <img style={{ marginRight: 10 }} src={dntIcon} alt="tooth" />
                <h4 className="card-title">Dental Offices</h4>
              </div>
              <h4 className="card-title">{new Intl.NumberFormat().format(dentistCount)}</h4>
            </div>
            <div style={styles.infoLine}>
              <div style={styles.infoLinePic}>
                <img style={{ marginRight: 10 }} src={hygienistIcon} alt="hygienist" />
                <h4 className="card-title">Dental Hygienist</h4>
              </div>
              <h4 className="card-title">{new Intl.NumberFormat().format(hygienistCount)}</h4>
            </div>
            <div style={styles.infoLine}>
              <div style={styles.infoLinePic}>
                <img style={{ marginRight: 10 }} src={hygienistIcon} alt="hygienist" />
                <h4 className="card-title">Dental Assistants</h4>
              </div>
              <h4 className="card-title">{new Intl.NumberFormat().format(dentalAssistantCount)}</h4>
            </div>
            <div style={styles.infoLine}>
              <div style={styles.infoLinePic}>
                <img style={{ marginRight: 10 }} src={hygienistIcon} alt="hygienist" />
                <h4 className="card-title">Assosiate Dentist</h4>
              </div>
              <h4 className="card-title">
                {new Intl.NumberFormat().format(associateDentistCount)}
              </h4>
            </div>
            <div style={styles.infoLine}>
              <div style={styles.infoLinePic}>
                <img
                  style={{ marginRight: 10, width: 26, height: 26 }}
                  src={greenCheckIcon}
                  alt="jobs-complete"
                />
                <h4 className="card-title">Completed Jobs</h4>
              </div>
              <h4>{new Intl.NumberFormat().format(completedJobsCount)}</h4>
            </div>
          </div>
        </div>
        <div
          className="card border-0"
          style={{ maxWidth: 750, marginTop: 20, alignItems: 'center' }}
        >
          <img style={{ maxWidth: 400 }} src={welcomeImage} alt="welcome" />
        </div>
      </div>
    </div>
  );
};

const styles = {
  infoLine: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '10px 0px',
    width: 500,
  },
  infoLinePic: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    height: 50,
  },
};
